import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, Button,
  FormControl, InputLabel, Select, MenuItem, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Paper, Chip, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FaEye, FaDownload, FaTrashAlt, FaRedo, FaUpload, FaFileExcel, FaFilePdf } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../Api";
import { openPdfWindow } from "../utils/pdfWindow";
import { getAppDateString } from "../utils/date";

const fmtSize = (b) => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;
const fmtDate = (ms) => { try { return new Date(ms).toLocaleString(); } catch { return "-"; } };
const todayIso = () => getAppDateString();              // YYYY-MM-DD
const isoToDDMMYYYY = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || ""); return m ? `${m[3]}-${m[2]}-${m[1]}` : ""; };

// Manage the uploaded Myntra & Flipkart print PDFs on the server (search / sort / view / delete).
export default function ArchiveFilesDialog({ open, onClose }) {
  const [platform, setPlatform] = useState("both");
  const [date, setDate] = useState("");            // "" = all dates
  const [dates, setDates] = useState({ myntra: [], flipkart: [] });
  const [search, setSearch] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyRel, setBusyRel] = useState("");
  const [sort, setSort] = useState({ key: "modified", dir: "desc" });
  const [upOpen, setUpOpen] = useState(false);
  const [upPlatform, setUpPlatform] = useState("flipkart");
  const [upIso, setUpIso] = useState(todayIso());
  const [upFiles, setUpFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showSubFiles, setShowSubFiles] = useState(false);

  const dateOptions = useMemo(() => {
    const set = new Set();
    if (platform === "both" || platform === "myntra") (dates.myntra || []).forEach(d => set.add(d));
    if (platform === "both" || platform === "flipkart") (dates.flipkart || []).forEach(d => set.add(d));
    if (platform === "both" || platform === "sku") (dates.sku || []).forEach(d => set.add(d));
    return [...set].sort((a, b) => {
      const [da, ma, ya] = a.split("-"), [db, mb, yb] = b.split("-");
      return `${yb}${mb}${db}`.localeCompare(`${ya}${ma}${da}`);
    });
  }, [dates, platform]);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (platform !== "both") params.platform = platform;
      if (date) params.date = date;
      if (search.trim()) params.search = search.trim();
      if (showSubFiles) params.sub = 1;
      const { data } = await api.get("/api/archive/files", { params });
      setFiles(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load files");
    } finally {
      setLoading(false);
    }
  }, [platform, date, search, showSubFiles]);

  useEffect(() => {
    if (!open) return;
    api.get("/api/archive/dates").then(({ data }) => setDates(data || { myntra: [], flipkart: [] })).catch(() => {});
  }, [open]);

  useEffect(() => { if (open) fetchFiles(); }, [open, platform, date, showSubFiles, fetchFiles]);

  const sorted = useMemo(() => {
    const arr = [...files];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      let va = a[key], vb = b[key];
      if (typeof va === "string") { va = va.toLowerCase(); vb = (vb || "").toLowerCase(); }
      const c = va < vb ? -1 : va > vb ? 1 : 0;
      return dir === "asc" ? c : -c;
    });
    return arr;
  }, [files, sort]);

  const setSortKey = (key) => setSort(s => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const openBlob = async (rel, download) => {
    const isXlsx = /\.xlsx?$/i.test(rel);
    const asDownload = download || isXlsx;   // xlsx can't render in-tab → download
    // Claim the view tab synchronously so popup blockers on other PCs don't drop it.
    const win = asDownload ? null : openPdfWindow();
    setBusyRel(rel);
    try {
      const { data } = await api.get("/api/archive/file", { params: { p: rel, ...(asDownload ? { dl: 1 } : {}) }, responseType: "blob" });
      const type = isXlsx
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";
      const name = rel.split("/").pop();
      const blob = new Blob([data], { type });
      if (asDownload) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        win.show(blob, name);
      }
    } catch (err) {
      if (win) win.fail();
      toast.error("Failed to open file");
    } finally { setBusyRel(""); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete "${row.name}" (${row.platform}/${row.date})?\nThis removes it from the server permanently.`)) return;
    setBusyRel(row.rel);
    try {
      await api.delete("/api/archive/file", { data: { p: row.rel } });
      toast.success("Deleted");
      setFiles(f => f.filter(x => x.rel !== row.rel));
      window.dispatchEvent(new CustomEvent("archive-file-deleted", {
        detail: { relpath: row.rel },
      }));
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    } finally { setBusyRel(""); }
  };

  useEffect(() => {
    if (!open) return undefined;
    const onArchiveFileDeleted = (event) => {
      const relpath = event?.detail?.relpath;
      if (relpath) setFiles((current) => current.filter((row) => row.rel !== relpath));
    };
    window.addEventListener("archive-file-deleted", onArchiveFileDeleted);
    return () => window.removeEventListener("archive-file-deleted", onArchiveFileDeleted);
  }, [open]);

  const doUpload = async () => {
    const dmy = isoToDDMMYYYY(upIso);
    if (!dmy) { toast.error("Pick a date."); return; }
    if (!upFiles.length) { toast.error("Choose file(s) to upload."); return; }
    const fd = new FormData();
    fd.append("platform", upPlatform);
    fd.append("date", dmy);
    [...upFiles].forEach(f => fd.append("files", f));
    setUploading(true);
    try {
      const { data } = await api.post("/api/archive/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.saved?.length) toast.success(data.message);
      else toast.warn(data.message || "Nothing uploaded");
      setUpOpen(false); setUpFiles([]);
      // jump the view to what we just uploaded (triggers a reload) + refresh dates
      setPlatform(upPlatform); setDate(dmy);
      api.get("/api/archive/dates").then((r) => setDates(r.data || {})).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.error || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const totalSize = sorted.reduce((s, f) => s + f.size, 0);
  const cols = [
    ["platform", "Platform"], ["date", "Date"], ["name", "File"],
    ["folder", "Folder"], ["pages", "Pages"], ["size", "Size"], ["modified", "Modified"],
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 800 }}>
        📁 Server Files — Myntra, Flipkart &amp; SKU
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Platform</InputLabel>
            <Select label="Platform" value={platform} onChange={(e) => { setPlatform(e.target.value); setDate(""); }}>
              <MenuItem value="both">All</MenuItem>
              <MenuItem value="myntra">Myntra</MenuItem>
              <MenuItem value="flipkart">Flipkart</MenuItem>
              <MenuItem value="sku">SKU (Excel)</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date</InputLabel>
            <Select label="Date" value={date} onChange={(e) => setDate(e.target.value)}>
              <MenuItem value="">All dates</MenuItem>
              {dateOptions.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="Search file name" value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") fetchFiles(); }} sx={{ minWidth: 220 }} />
          <Button variant="contained" size="small" startIcon={<FaRedo />} onClick={fetchFiles} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </Button>
          <Button variant="contained" color="success" size="small" startIcon={<FaUpload />} onClick={() => setUpOpen(true)}>
            Upload
          </Button>
          <Button
            variant={showSubFiles ? "contained" : "outlined"}
            size="small"
            onClick={() => setShowSubFiles((v) => !v)}
            sx={{ textTransform: "none" }}
          >
            {showSubFiles ? "Hide Sub Files" : "Show Sub Files"}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Chip label={`${sorted.length} file(s) · ${fmtSize(totalSize)}`} />
        </Box>

        {/* Manual upload: pick platform + date, drop .pdf/.xlsx files in */}
        <Dialog open={upOpen} onClose={() => !uploading && setUpOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 800 }}>
            ⬆ Upload files
            <IconButton onClick={() => setUpOpen(false)} size="small" disabled={uploading}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select label="Platform" value={upPlatform} onChange={(e) => setUpPlatform(e.target.value)}>
                  <MenuItem value="myntra">Myntra</MenuItem>
                  <MenuItem value="flipkart">Flipkart</MenuItem>
                  <MenuItem value="sku">SKU (Excel)</MenuItem>
                </Select>
              </FormControl>
              <TextField size="small" fullWidth type="date" label="Date"
                value={upIso} onChange={(e) => setUpIso(e.target.value)} InputLabelProps={{ shrink: true }} />
              <Button variant="outlined" component="label">
                {upFiles.length ? `${upFiles.length} file(s) selected` : "Choose files (.pdf / .xlsx)"}
                <input hidden multiple type="file" accept=".pdf,.xlsx"
                  onChange={(e) => setUpFiles([...e.target.files])} />
              </Button>
              {upFiles.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                  {[...upFiles].map(f => f.name).join(", ")}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Goes to <code>pdf-archive/{upPlatform}/{isoToDDMMYYYY(upIso) || "…"}/</code>
              </Typography>
              <Button variant="contained" color="success" startIcon={<FaUpload />}
                disabled={uploading || !upFiles.length} onClick={doUpload}>
                {uploading ? "Uploading…" : `Upload ${upFiles.length || ""}`}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        <TableContainer component={Paper} sx={{ maxHeight: 460 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, backgroundColor: "#f3f4f6" } }}>
                {cols.map(([k, label]) => (
                  <TableCell key={k} sortDirection={sort.key === k ? sort.dir : false}
                    align={k === "size" || k === "pages" ? "right" : "left"}>
                    <TableSortLabel active={sort.key === k} direction={sort.key === k ? sort.dir : "asc"}
                      onClick={() => setSortKey(k)}>{label}</TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "gray" }}>
                  {loading ? "Loading…" : "No files."}
                </TableCell></TableRow>
              ) : sorted.map((row) => (
                <TableRow key={row.rel} hover>
                  <TableCell>
                    <Chip size="small" label={row.platform}
                      color={row.platform === "flipkart" ? "primary" : row.platform === "sku" ? "warning" : "secondary"} variant="outlined" />
                  </TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell sx={{ fontWeight: 600, wordBreak: "break-all" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ display: "flex", color: /\.xlsx?$/i.test(row.name) ? "success.main" : "error.main" }}>
                        {/\.xlsx?$/i.test(row.name) ? <FaFileExcel /> : <FaFilePdf />}
                      </Box>
                      <span>{row.name}</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{row.folder === "." ? "—" : row.folder}</TableCell>
                  <TableCell align="right">{Number.isFinite(Number(row.pages)) && Number(row.pages) > 0 ? row.pages : "—"}</TableCell>
                  <TableCell align="right">{fmtSize(row.size)}</TableCell>
                  <TableCell>{fmtDate(row.modified)}</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="View"><span>
                      <IconButton size="small" color="info" disabled={busyRel === row.rel} onClick={() => openBlob(row.rel, false)}><FaEye /></IconButton>
                    </span></Tooltip>
                    <Tooltip title="Download"><span>
                      <IconButton size="small" disabled={busyRel === row.rel} onClick={() => openBlob(row.rel, true)}><FaDownload /></IconButton>
                    </span></Tooltip>
                    <Tooltip title="Delete"><span>
                      <IconButton size="small" color="error" disabled={busyRel === row.rel} onClick={() => del(row)}><FaTrashAlt /></IconButton>
                    </span></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
}
